"use client";

import { createContext, type ReactNode, useContext } from "react";
import { locales } from "@/i18n/locales";
import type { AppBrand } from "@/modules/app-brand";
import { DEFAULT_MODE } from "@/themes/default-theme";
import { THEME_IDS } from "@/themes/theme-ids";

/** Without a provider the bundled look is used. */
export const AppBrandContext = createContext<AppBrand>({ logo: null, logoDark: null, iconSet: null, css: "", defaultMode: DEFAULT_MODE });

/** Hands the app's own logos, read on the server, to the client components below. */
export function AppBrandProvider({ brand, children }: { brand: AppBrand; children: ReactNode }) {
  return <AppBrandContext value={brand}>{children}</AppBrandContext>;
}

/** What the app offers people: the enabled languages and colour themes (set in the Settings app). */
type AppOffers = { languages: readonly string[]; themes: readonly string[] };

/** Without a provider everything supported is offered. */
const AppOffersContext = createContext<AppOffers>({ languages: locales, themes: THEME_IDS });

/** Hands the enabled languages and themes, read on the server, to the client components below. */
export function AppOffersProvider({ offers, children }: { offers: AppOffers; children: ReactNode }) {
  return <AppOffersContext value={offers}>{children}</AppOffersContext>;
}

export function useEnabledLanguages(): readonly string[] {
  return useContext(AppOffersContext).languages;
}

export function useEnabledThemes(): readonly string[] {
  return useContext(AppOffersContext).themes;
}
